/**
 * Runtime re-theming.
 *
 * The web apps and the kiosk load their brand configuration at runtime, not at build time
 * (spec/03-architecture.md §12). Because every token is a CSS variable and every component and
 * Tailwind utility reads one, re-theming is just writing those variables — no rebuild, no restart,
 * and it can be done per venue inside one running console.
 *
 *   applyBrandTokens({ shared: { accent: '#7C3AED' }, light: { 'accent-ink': '#5B21B6' } })
 *
 * The stylesheet it writes uses exactly the selectors the generated defaults use, so a brand
 * override has the same specificity and wins on source order. For server rendering, call
 * brandTokensToCss() and put the result in a <style> in the document head, which avoids a flash of
 * the default theme.
 */
import { cssVar, themeRuleTargets } from '../tokens.generated.js';
const declarations = (overrides) => Object.entries(overrides)
    .map(([name, value]) => `  ${cssVar(name)}: ${value};`)
    .join('\n');
/**
 * Where each group's declarations go. At :root these are the generated defaults' own selectors,
 * copied from the token source, so an override matches them exactly and wins on source order. A
 * scoped override re-states them around the scope instead; a scope whose *ancestor* forces dark
 * while the OS prefers light is the one case it does not cover, which is fine for what a scope is
 * for — previewing one venue's brand inside a console.
 */
const targetsFor = (group, scope) => {
    if (scope === ':root') {
        // A mode the tokens do not define has nowhere to land.
        const targets = themeRuleTargets;
        return targets[group] ?? [];
    }
    if (group === 'shared')
        return [{ media: null, selectors: [scope] }];
    const attr = `[data-theme="${group}"]`;
    const explicit = { media: null, selectors: [`${scope}${attr}`, `${attr} ${scope}`] };
    return group === 'light'
        ? [
            {
                media: '(prefers-color-scheme: light)',
                selectors: [`${scope}:not([data-theme="dark"])`],
            },
            explicit,
        ]
        : [explicit];
};
/** The stylesheet for a set of brand overrides. Deterministic — safe to render on the server. */
export function brandTokensToCss(overrides, options = {}) {
    const scope = options.scope ?? ':root';
    const blocks = [];
    for (const group of ['shared', 'dark', 'light']) {
        const values = overrides[group];
        if (!values || Object.keys(values).length === 0)
            continue;
        for (const target of targetsFor(group, scope)) {
            const rule = `${target.selectors.join(',\n')} {\n${declarations(values)}\n}`;
            blocks.push(target.media ? `@media ${target.media} {\n${rule}\n}` : rule);
        }
    }
    return blocks.join('\n\n');
}
/**
 * Write the overrides into the document and return a function that removes them again.
 *
 * Calling it again with the same `id` replaces the previous values rather than stacking, so a
 * console can switch between venues without leaking one brand into the next.
 */
export function applyBrandTokens(overrides, options = {}) {
    const doc = options.document ?? globalThis.document;
    if (!doc) {
        throw new Error('applyBrandTokens() needs a document. On the server, render brandTokensToCss() into a <style> instead.');
    }
    const id = options.id ?? 'clavian-brand';
    const existing = doc.querySelector(`style[data-clavian-brand="${id}"]`);
    const style = existing ?? doc.createElement('style');
    style.setAttribute('data-clavian-brand', id);
    style.textContent = brandTokensToCss(overrides, options);
    if (!existing)
        doc.head.append(style);
    return () => {
        style.remove();
    };
}
/** What a token resolves to right now, for canvas, charts and anything outside CSS. */
export function resolveToken(name, element) {
    const target = element ?? globalThis.document?.documentElement;
    if (!target)
        return '';
    return getComputedStyle(target).getPropertyValue(cssVar(name)).trim();
}
//# sourceMappingURL=brand.js.map